/**
 * `AW4` · Cómo se muestran los valores de la tabla «Conversaciones». Sin lógica
 * de datos.
 *
 * Está aparte de `tabla.tsx` para que ese archivo quede con las decisiones que
 * lleva `guard` y nada más.
 */

import type { EstadoMensaje } from "@contracts/conversation/index.ts"

/**
 * El idioma con el que se formatean fechas y números.
 *
 * Fijo y no de configuración: el operador de esta herramienta es de Elige
 * Educar, en Chile. Tomarlo del navegador haría que la misma fecha se viera
 * distinta según la máquina, que es peor que una decisión explícita.
 */
const LOCALE = "es-CL"

/**
 * ⭐ RASTRO · 17/9/2026 · `F5-CA-92` — acá decía que la ZONA HORARIA se dejaba
 * «en la del navegador a propósito», y era exactamente lo contrario de lo que
 * se exige: el PRD pide las fechas en hora de Chile, y la demo lo afirma con el
 * navegador en UTC —«Consulta de Bruno» sigue mostrando `10-09-2026 09:15`—.
 * Fijarla no le muestra la hora de Santiago a alguien de otro huso «creyendo
 * leer la suya»: le muestra LA HORA DEL PRODUCTO, que es la única que sirve
 * para reconciliar un mensaje con lo que reporta Meta. El comentario viejo lo
 * leía un humano; ningún gate lo miraba, y la prosa contradecía al criterio.
 */
const ZONA_HORARIA = "America/Santiago"

/**
 * `dd-mm-aaaa hh:mm`, en 24 h y en hora de Chile (`F5-CA-90`, `F5-CA-92`).
 *
 * El formato se COMPONE desde `formatToParts` y no se toma del `format()` del
 * locale, por dos cosas que el criterio fija y el locale no:
 *
 * - `es-CL` separa la fecha de la hora con una COMA (`10-09-2026, 09:15`) y el
 *   Anexo B la quita para todas las pantallas (`10-09-2026 09:15`).
 * - El reloj de 12/24 h y el sufijo `a. m.` / `p. m.` NO son iguales en todos
 *   los motores de ICU para el mismo locale; `hourCycle: "h23"` lo fija.
 */
const FORMATO_FECHA = new Intl.DateTimeFormat(LOCALE, {
  timeZone: ZONA_HORARIA,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
})

/**
 * Lo que se muestra cuando el valor legítimamente no está.
 *
 * Un guión y no una celda vacía: una celda vacía se lee como «se rompió algo»,
 * y estos casos son normales. Varios campos de esta tabla pueden ser «no
 * aplica» según el tipo de mensaje, y el guión los distingue de un dato
 * realmente faltante.
 */
export const SIN_VALOR = "—"

/**
 * La fecha y hora del mensaje, en `dd-mm-aaaa hh:mm` y en hora de Chile.
 *
 * Una fecha inválida se muestra como tal en vez de romper la fila. Llega así
 * sólo si el borde mandó algo que no es una fecha, y esconderlo detrás de un
 * guión lo haría indistinguible de un campo vacío legítimo.
 */
export function formatearFecha(fecha: Date): string {
  if (Number.isNaN(fecha.getTime())) return "fecha inválida"
  const partes = FORMATO_FECHA.formatToParts(fecha)
  /*
   * Se lee cada parte por su nombre y los separadores se escriben acá: el guión
   * de la fecha, el espacio entre fecha y hora y los dos puntos del reloj son
   * del criterio (`F5-CA-90`), no del locale. Así una coma o un `p. m.` de ICU
   * no se cuelan en la celda.
   */
  const valor = (tipo: string): string =>
    partes.find((parte) => parte.type === tipo)?.value ?? ""
  return `${valor("day")}-${valor("month")}-${valor("year")} ${valor("hour")}:${valor("minute")}`
}

/**
 * Origen del mensaje.
 *
 * El PRD define `es_sistema` como: `sí` = chatbot, `no` = usuario. Se escribe
 * en minúscula a propósito, porque es una etiqueta corta dentro de la celda y
 * no el inicio de una oración.
 */
export function formatearEsSistema(esSistema: boolean): string {
  return esSistema ? "sí" : "no"
}

/**
 * Cantidad de fuentes consultadas por el RAG para responder.
 *
 * **Invariante del contrato:** `fuentes_consultadas === null` ⟺
 * `es_sistema === false`. Es decir, un mensaje del usuario nunca muestra `0`;
 * muestra el guión de `SIN_VALOR` porque para él la pregunta no aplica.
 *
 * `0`, en cambio, es un valor legítimo para un mensaje del chatbot: significa
 * que respondió sin consultar ninguna fuente. Colapsar `0` con `null` ocultaría
 * exactamente el caso que más vale la pena revisar.
 */
export function formatearFuentesConsultadas(fuentes: number | null): string {
  return fuentes === null ? SIN_VALOR : String(fuentes)
}

/**
 * ⭐ `R3-UI1` · **LAS CUATRO ETIQUETAS DE Estado, EN ESPAÑOL Y CON MAYÚSCULA
 * INICIAL.** `PRD_R3.md:713`, textual: *«En los mensajes del sistema, la
 * etiqueta «Enviado», «Entregado», «Leído» o «Fallido»; en «Fallido», el motivo
 * en palabras debajo de la etiqueta. En los del participante, «—»»*.
 *
 * ⛔ **La clave es el estado GUARDADO, que es el vocabulario de `dispatch`
 * (`RECIPIENT_STATES`: `enviado`, `entregado`, `fallido`, `leído`), y NO el
 * `sent`/`delivered`/`read`/`failed` de Meta.** Quien traduce el aviso de Meta al
 * vocabulario guardado es quien recibe el webhook (`PRD_R3.md:735-742`, columna
 * «Aviso de Meta» → «Estado que se muestra»); acá sólo se pone la MAYÚSCULA.
 * `contracts/conversation` `LineaAceptadaPorMeta.estado` lo fija en `"enviado"`
 * como literal y dice de dónde sale el valor.
 *
 * ⛔ **Esto NO es la traducción que el guard B3/RT 4 prohíbe.** Lo prohibido es
 * una segunda tabla **código de error → motivo en palabras** (`R3-F1-CA-108`):
 * eso vive en `reasonFor` de `contracts/channel`, lo resuelve el servidor y
 * llega hecho en `FilaDeMensaje.motivo`. Esta tabla es estado→ETIQUETA, que
 * `PRD_R3.md:713` declara de presentación y que ningún otro lado escribe.
 *
 * ⚠️ **La clave se busca plegada** (sin mayúsculas y sin tildes) porque `leído`
 * viaja con tilde y puede llegar en NFD: `"leído"` con la tilde descompuesta se
 * ve idéntica y **no es igual**. Es la misma lección que el `normalize("NFC")` de
 * `gates/r1/R3-UI1.gate.ts`.
 */
const ETIQUETA_DEL_ESTADO: Readonly<Record<string, string>> = {
  enviado: "Enviado",
  entregado: "Entregado",
  leido: "Leído",
  fallido: "Fallido",
}

/** Sin mayúsculas y sin tildes, para que `leído`/`LEIDO`/`leido` sean la misma clave. */
function plegado(valor: string): string {
  return valor
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
}

/**
 * Último estado de entrega reportado por Meta, **con la etiqueta de R3**.
 *
 * ⟦tres-ausencias-y-no-una⟧: el contrato modela tres casos separados. ⭐ `R3-UI1`
 * los pinta de **dos** maneras y no de tres, y eso es un cambio de criterio con
 * fuente:
 *
 * - `no_aplica`: el mensaje es del participante. La entrega no aplica ⇒ `SIN_VALOR`.
 * - `sin_reporte`: ⭐ ~~«sin reporte»~~ → `SIN_VALOR`. `PRD_R3.md:731`, textual:
 *   *«Las filas del sistema guardadas antes de R3 no se migran ni se completan:
 *   muestran el estado que tengan guardado, **o «—» en Estado** y `estado_mensaje`
 *   vacío en el CSV si no tienen ninguno»*, y `R3-F1-CA-50` lo mide sobre la fila
 *   «Respuesta sin estado». **Los dos casos se ven IGUAL en pantalla y siguen
 *   siendo dos en el contrato**: la distinción que ⟦tres-ausencias-y-no-una⟧
 *   compra —«nunca va a tener estado» vs. «todavía no»— sigue viva del lado del
 *   dato, que es donde sirve; lo que R3 decidió es que el operador no la lea en
 *   una columna de diez.
 * - `reportado`: ⭐ ~~el texto literal, sin traducir~~ → la etiqueta de
 *   `ETIQUETA_DEL_ESTADO`. `R3-F1-CA-46`/`47`/`48` piden «Leído», «Entregado» y
 *   «Enviado» con mayúscula; el crudo `enviado` los deja rojos a los tres.
 *
 * ⚠️ **Un estado que no está en la tabla se muestra TAL CUAL, y es a propósito.**
 * `EstadoReportado.estado` es `string` abierto porque *«el conjunto es de Meta y
 * no nuestro»* (`contracts/conversation`), así que colapsarlo a «—» borraría un
 * dato real y colapsarlo a «Fallido» inventaría uno. Lo único honesto con un
 * estado desconocido es mostrarlo.
 */
export function formatearEstadoMensaje(estado: EstadoMensaje): string {
  switch (estado.caso) {
    case "no_aplica":
      return SIN_VALOR
    case "sin_reporte":
      return SIN_VALOR
    case "reportado":
      return ETIQUETA_DEL_ESTADO[plegado(estado.estado)] ?? estado.estado
  }
}
